import { useEffect, useState } from 'react'
import { IconArrowRight } from '@tabler/icons-react'
import { Row, Col, Form, FormFeedback, FormGroup, Label, Container, Spinner } from 'reactstrap'
import { useForm } from 'react-hook-form'
import { Select } from '../../@core/components/select'
import classnames from 'classnames'
import styled from 'styled-components'
import mapper from '../../_endpoints'
import LandingStepper from './LandingStepper'
import { selectThemeColors } from '../../utility/Utils'
import { useFetch } from '../../_hooks/useFetch'
import { PERSONAL_INFORMATION } from '../../_enums'
import { usePost } from '../../_hooks/usePost'
import { useNavigate } from 'react-router-dom'
import { useFeatureGate } from '../../_hooks/useFeatureGate'
import { isNullOrUndefined } from '@microsoft/applicationinsights-core-js'
import { setProfile } from '../../_redux/actions/profileActions'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Checkbox } from 'dsm-react-lib'

const AccountInformation = styled.div`
  font-size: 24px;
  line-height: 34px;
  font-weight: 600;
  font-style: normal;
`
const AccountDetails = styled.div`
  color: var(--border-strong);
  line-height: 21px;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  font-family: 'DM Sans';
  padding-top: 1rem;
`

const ConsentMessage = styled.div`
  margin: -0.3rem 0 0 0;
`

const ConsentError = styled.div`
  color: var(--color-secondary);
  font-size: 12px;
  margin-top: -1rem;
`

const validationMessages = {
  assetClasses: 'Asset classes are mandatory',
  jobFunction: 'Job function is mandatory',
  consent: 'Providing consent is mandatory'
}

export default function LoginAccount({ menuItems, activeItemId, setActiveItemId }) {
  const [, getJobsData] = useFetch()
  const [, getMultiSelectData] = useFetch()
  const [isSubmitting, saveUserDetailsData] = usePost()
  const [assetClasses, setAssetClasses] = useState([])
  const [jobs, setJobs] = useState()
  const [areas, setAreas] = useState()
  const [sectors, setSectors] = useState()
  const [selectedAssetClasses, setSelectedAssetClasses] = useState()
  const [selectedJob, setSelectedJob] = useState()
  const [selectedAreas, setSelectedAreas] = useState()
  const [selectedSectors, setSelectedSectors] = useState()
  const [hasAgreed, setHasAgreed] = useState()
  const { trigger } = useForm({})
  const { displayName: name } = JSON.parse(localStorage.getItem('userInfoData'))
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const profile = useSelector((state) => state.profileReducer.profile)
  const { value: centrali3_0 } = useFeatureGate('centrali3_0', false)

  // Dynamic main route based on feature flag
  const dynamicMainRoute = centrali3_0 ? '/my-space' : '/app-store'

  useEffect(() => {
    const getOptions = async () => {
      const response = await getMultiSelectData(mapper.multiSelect.get)
      const assetClasses = response
        .filter((option) => option.selectType === PERSONAL_INFORMATION.ASSET_CLASSES)
        .map(({ value, id }) => {
          return {
            label: value,
            value: id
          }
        })

      const interestedAreas = response
        .filter((option) => option.selectType === PERSONAL_INFORMATION.INTERESTED_AREA)
        .map(({ value, id }) => {
          return {
            label: value,
            value: id
          }
        })

      const interestedSectors = response
        .filter((option) => option.selectType === PERSONAL_INFORMATION.INTERESTED_SECTOR)
        .map(({ value, id }) => {
          return {
            label: value,
            value: id
          }
        })

      setAssetClasses(assetClasses)
      setAreas(interestedAreas)
      setSectors(interestedSectors)
    }

    const getJobs = async () => {
      const response = await getJobsData(mapper.jobs.get)
      const options = response.map((job) => {
        return {
          label: job.name,
          value: job.id
        }
      })
      setJobs(options)
    }

    getOptions()
    getJobs()
  }, [])

  const { handleSubmit } = useForm({})

  const onAssetClassChange = (selectedOption) => {
    setSelectedAssetClasses(selectedOption)
  }

  const onSubmitHandler = async () => {
    if (selectedAssetClasses === undefined) setSelectedAssetClasses([])
    if (selectedJob === undefined) setSelectedJob(null)
    if (hasAgreed === undefined) setHasAgreed(null)

    const isValid = await trigger()
    if (
      isValid &&
      selectedAssetClasses !== undefined &&
      selectedAssetClasses?.length > 0 &&
      !isNullOrUndefined(selectedJob) &&
      hasAgreed
    ) {
      const assetClasses =
        selectedAssetClasses?.map((assetClass) => {
          return {
            id: assetClass.value,
            name: assetClass.label
          }
        }) || []
      const areas =
        selectedAreas?.map((area) => {
          return {
            id: area.value,
            name: area.label
          }
        }) || []
      const sectors =
        selectedSectors?.map((sector) => {
          return {
            id: sector.value,
            name: sector.label
          }
        }) || []

      const response = await saveUserDetailsData({
        url: mapper.bff.saveUserDetails,
        data: {
          tourFinished: true,
          jobFunction: {
            id: selectedJob.value,
            name: selectedJob.label
          },
          userSelectedOptions: [...assetClasses, ...areas, ...sectors]
        }
      })

      const userData = JSON.parse(localStorage.getItem('userInfoData'))
      localStorage.setItem(
        'userInfoData',
        JSON.stringify({
          ...userData,
          jobFunction: { id: selectedJob.value, name: selectedJob.label },
          isInInvestmentDesktop: true
        })
      )

      const userInfoData = {
        ...profile,
        jobFunction: { id: selectedJob.value, name: selectedJob.label },
        isInInvestmentDesktop: true
      }
      dispatch(setProfile(userInfoData))

      if (response && response.status === 'Success') {
        navigate(dynamicMainRoute, { checkProfileCompletion: true })
      }
    }
  }

  return (
    <>
      <Container fluid className="p-0 mb-2 m-0 w-100">
        <LandingStepper
          menuItems={menuItems}
          activeItemId={activeItemId}
          setActiveItemId={setActiveItemId}
        />
        <hr className="mt-1" />
        <Row className="p-0 mt-0 mb-2">
          <Col>
            <AccountInformation>{`Welcome ${name?.split(',')[1]}!`}</AccountInformation>
            <AccountDetails>
              {'Please answer the questions below to get the best experience on the portal'}
            </AccountDetails>
          </Col>
        </Row>
        <Form onSubmit={handleSubmit(onSubmitHandler)}>
          <Row className="mb-1">
            <Col md={6}>
              <div>
                <Label className="form-label text-muted" for="what-team-do-you-belong-to">
                  Which asset classes are you interested in?
                </Label>
                <Select
                  isClearable={false}
                  isMulti
                  name="assetClasses"
                  menuShouldBlockScroll={true}
                  options={assetClasses}
                  defaultValue={selectedAssetClasses}
                  onChange={onAssetClassChange}
                  classNamePrefix="select"
                  placeholder="Select asset classes"
                  theme={selectThemeColors}
                  menuPlacement="auto"
                  styles={{
                    option: (style) => ({ ...style, color: 'var(--text-heading)' }),
                    menuPortal: (base) => ({ ...base, zIndex: 999 })
                  }}
                  className={classnames('react-select', {
                    'is-invalid bw-5': selectedAssetClasses?.length === 0
                  })}
                />
                {selectedAssetClasses?.length === 0 && (
                  <FormFeedback invalid={1}>{validationMessages.assetClasses}</FormFeedback>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div>
                <Label className="form-label text-muted" for="job-function">
                  What is your job function?
                </Label>
                <Select
                  isClearable={false}
                  name="jobFunction"
                  options={jobs}
                  menuShouldBlockScroll={true}
                  defaultValue={selectedJob}
                  onChange={(selectedOption) => setSelectedJob(selectedOption)}
                  classNamePrefix="select"
                  placeholder="Select job function"
                  menuPlacement="auto"
                  theme={selectThemeColors}
                  styles={{ option: (style) => ({ ...style, color: 'var(--text-heading)' }) }}
                  className={classnames('react-select', {
                    'is-invalid': selectedJob === null
                  })}
                />
                {selectedJob === null && (
                  <FormFeedback invalid={1}>{validationMessages.jobFunction}</FormFeedback>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <div className="mb-1">
                <Label className="form-label text-muted" for="job-function">
                  What areas are you interested in?
                </Label>
                <Select
                  isClearable={false}
                  name="areas"
                  isMulti
                  hideSelectedOptions={false}
                  menuShouldBlockScroll={true}
                  options={areas}
                  onChange={(selectedOption) => setSelectedAreas(selectedOption)}
                  classNamePrefix="select"
                  placeholder="Select areas"
                  showSelectedAsChecked={true}
                  theme={selectThemeColors}
                  menuPlacement="auto"
                  styles={{
                    option: (style) => ({ ...style, color: 'var(--text-heading)' }),
                    menuPortal: (base) => ({ ...base, zIndex: 999 })
                  }}
                  className={classnames('react-select', {
                    'is-invalid': selectedAreas === null
                  })}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FormGroup>
                <Label className="form-label text-muted" for="job-function">
                  What sectors are you interested in?
                </Label>
                <Select
                  isClearable={false}
                  name="sectors"
                  isMulti
                  menuShouldBlockScroll={true}
                  options={sectors}
                  onChange={(selectedOption) => setSelectedSectors(selectedOption)}
                  classNamePrefix="select"
                  placeholder="Select sectors"
                  theme={selectThemeColors}
                  menuPlacement="auto"
                  styles={{
                    option: (style) => ({ ...style, color: 'var(--text-heading)' }),
                    menuPortal: (base) => ({ ...base, zIndex: 999 })
                  }}
                  className={classnames('react-select', {
                    'is-invalid': selectedSectors === null
                  })}
                />
              </FormGroup>
            </Col>
          </Row>
          <div>
            <Col md={12}>
              <div className="d-flex mb-2 align-items-center">
                <Checkbox
                  color="secondary"
                  variant="custom"
                  id="consentCheckbox"
                  name="consentCheckbox"
                  onChange={(e) => setHasAgreed(e.target.checked)}
                />
                <ConsentMessage>
                  By signing up you consent to the secure storage and processing data to improve
                  your user experience.
                </ConsentMessage>
              </div>
              {hasAgreed !== undefined && !hasAgreed && (
                <ConsentError>{validationMessages.consent}</ConsentError>
              )}
            </Col>
          </div>
          <div className="d-flex justify-content-end text-end mt-2">
            <Button
              color="secondary"
              className="btn-next"
              type="submit"
              disabled={isSubmitting}
              label={isSubmitting ? 'Submitting...' : 'Continue'}
              icon={
                isSubmitting ? (
                  <Spinner classNAme="align-iems-center" size="sm" />
                ) : (
                  <IconArrowRight size={14} className="align-middle ml-sm-25 ml-0" />
                )
              }
              iconPosition="end"></Button>
          </div>
        </Form>
      </Container>
    </>
  )
}
